import { baseApi } from './baseApi';
import { PaymentConfig } from './paymentConfigApi';

export interface Asset {
  id: string;
  name: string;
  apiKey: string;
  code?: string;
  isActive?: boolean;
  insertDtr?: boolean;
  insertAotm?: boolean;
  webhookUrl?: string;
  webhookTimeout?: number;
  callbackSuccess?: string;
  callbackFailure?: string;
  proxyUrl?: string;
  amc: { id: string };
  upiAutopayPendingHtml?: string;
  allowedIps?: string;
  createdAt?: string;
  updatedAt?: string;
  isUsingDynamicGatewayRouting?: boolean;
  config?: PaymentConfig[];
}

/**
 * Utility function to remove null or undefined values from an object
 */
export function removeNullOrUndefined(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== null && value !== undefined)
  );
}

export const assetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAssets: builder.query<Asset[], string>({
      query: (amcId) => `/assets/amc/${amcId}`,
      providesTags: (_result, _err, amcId) => [{ type: 'Asset', id: amcId }],
    }),

    // Asset dropdown for payments page
    // Backend returns: { data: Asset[] }
    getAssetsDropdown: builder.query<{ data: Asset[] }, string | void>({
      query: (amcId) => ({
        url: '/assets/dropdown',
        params: amcId ? { amcId } : undefined,
      }),
      providesTags: (_result, _err, amcId) => [{ type: 'Asset', id: amcId || 'dropdown' }],
    }),

    getAssetById: builder.query<Asset, string>({
      query: (assetId) => `/assets/${assetId}`,
      providesTags: (_result, _err, assetId) => [{ type: 'Asset', id: assetId }],
    }),

    enableDynamicRouting: builder.mutation<void, string>({
      query: (assetId) => ({
        url: `/assets/enable-dynamic-routing/${assetId}`,
        method: "POST",
      }),
    }),

    createAsset: builder.mutation<Asset, Omit<Asset, 'id'>>({
      query: (asset) => ({
        url: `/assets`,
        method: 'POST',
        body: asset,
      }),
      invalidatesTags: (_result, _err, { amc }) => [{ type: 'Asset', id: amc.id }],
    }),

    updateAsset: builder.mutation<Asset, Partial<Asset> & Pick<Asset, 'id' | 'amc'>>({
      query: ({ id, amc, ...patch }) => ({
        url: `/assets/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _err, { id, amc }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: amc.id },
      ],
    }),

    deleteAsset: builder.mutation<void, string>({
      query: (id) => ({
        url: `/assets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, id) => [{ type: 'Asset', id }],
    }),
    getAssetLogs: builder.query<any, string>({
      query: (id) => `/assets/logs/${id}`,
    }),

    getIpSuggestions: builder.query<string[], string>({
      query: (assetId) => `/assets/ip-suggestion/${assetId}`,
    }),

    disableAsset: builder.mutation<void, { id: string; amcId: string }>({
      query: ({ id }) => ({
        url: `/assets/disable/${id}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, { amcId }) => [{ type: 'Asset', id: amcId }],
    }),

    enableAsset: builder.mutation<void, { id: string; amcId: string }>({
      query: ({ id }) => ({
        url: `/assets/enable/${id}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, { amcId }) => [{ type: 'Asset', id: amcId }],
    }),
  }),
});

export const {
  useGetAssetsQuery,
  useGetAssetsDropdownQuery,
  useCreateAssetMutation,
  useGetAssetByIdQuery,
  useEnableDynamicRoutingMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useGetAssetLogsQuery,
  useGetIpSuggestionsQuery,
  useDisableAssetMutation,
  useEnableAssetMutation,
} = assetApi;
